import {
    addDays,
    differenceInCalendarDays,
    safeFormatClientDate,
    safeFormatISODate,
    safeParseClientDate,
    safeParseISO,
    subDays,
} from 'shared/util/datetime-fns';
import type { BoundingBox, CheckboxMap, Destination, FlexibleDate, Month, NumberRange } from '@/interfaces';

const isLegalMonth = (month: number, year: number): boolean => {
    if (month < 1 || month > 12) {
        return false;
    }

    const firstMonth = new Date().getMonth() + 1;
    const firstYear = new Date().getFullYear();

    if (year < firstYear || year > firstYear + 1) {
        return false;
    }
    if (month < firstMonth && year === firstYear) {
        return false;
    }
    if (month >= firstMonth && year === firstYear + 1) {
        return false;
    }

    return true;
};

export const queryToCheckboxMap = (toMap: string[] | string): CheckboxMap =>
    Array.isArray(toMap)
        ? toMap.reduce((map: CheckboxMap, item: string): CheckboxMap => {
              map[item] = true;
              return map;
          }, {})
        : { [toMap]: true };

export const queryToString = (asString: string[] | string): string => (Array.isArray(asString) ? asString[0] : asString);

export const queryToList = (asString: string[] | string): string[] => (Array.isArray(asString) ? asString : [asString]);

export const queryToNumberRange = (toRange: string[] | string): NumberRange | null => {
    const [start, end] = (Array.isArray(toRange) ? toRange[0] : toRange).split('-');
    return ~~end ? [~~start, ~~end] : null;
};

export const queryToFlexibleDate = (toRange: string[] | string): FlexibleDate => {
    const [start, end] = (Array.isArray(toRange) ? toRange[0] : toRange).split('-');
    const parsedStart = safeParseClientDate(start);

    if (end && parsedStart) {
        const parsedEnd = safeParseClientDate(end);
        if (parsedEnd) {
            const diff = differenceInCalendarDays(parsedStart, parsedEnd);

            if (diff) {
                const flexibility = diff % 2 !== 0 ? (diff + 1) / 2 : diff / 2;

                return {
                    date: safeFormatISODate(addDays(flexibility, parsedStart)) || undefined,
                    flexibility,
                };
            }
        }
    }

    return { date: safeFormatISODate(parsedStart) || undefined, flexibility: 0 };
};

export const queryToMonths = (toMonths: string[] | string): Month[] => {
    const months = Array.isArray(toMonths) ? toMonths : [toMonths];

    return months
        .map((entry: string) => {
            const [month, year] = entry.split('.');
            return isLegalMonth(~~month, ~~year) ? { month: ~~month, year: ~~year } : null;
        })
        .filter((entry: Month | null) => entry !== null) as Month[];
};

export const stringToQuery = (text: string): string => text;

export const listToQuery = (list: string[]): string[] => list;

export const checkboxMapToQuery = (map: CheckboxMap): string[] | null => {
    const values = Object.keys(map).filter((key) => map[key]);
    return values.length === 0 ? null : values;
};

export const numberRangeToQuery = (range?: NumberRange): string | null => (range ? `${range[0]}-${range[1]}` : null);

export const flexibleDateToQuery = (flexDate?: FlexibleDate): string | null => {
    if (flexDate?.date) {
        const parsedDate = safeParseISO(flexDate.date);

        if (parsedDate && flexDate.flexibility) {
            const startDate = safeFormatClientDate(subDays(flexDate.flexibility, parsedDate));
            const endDate = safeFormatClientDate(addDays(flexDate.flexibility, parsedDate));

            return `${startDate}-${endDate}`;
        }
        return safeFormatClientDate(parsedDate, null);
    }
    return null;
};

export const monthsToQuery = (months: Month[]): string[] | null => (months.length === 0 ? null : months.map((e) => `${e.month}.${e.year}`));

export const queryToNumber = (numString?: string): number | undefined => {
    if (!numString) return undefined;
    const parsed = Number.parseInt(numString);
    return Number.isNaN(parsed) ? undefined : parsed;
};

export const queryToNumbers = (numString?: string | string[]): number[] | undefined => {
    const list = Array.isArray(numString) ? numString : numString?.split(',');
    const filtered = list?.map(queryToNumber).filter((it) => it !== undefined);
    return filtered?.length ? filtered : undefined;
};

export const numberToQuery = (num?: number) => (!num || Number.isNaN(num) ? undefined : num.toString());

export const numbersToQuery = (numbers?: number[]) => (numbers?.length ? numbers.join(',') : undefined);

const DESTINATION_DELIMITER = '|';

export const parseBoundingBox = (str: string): BoundingBox | undefined => {
    const parts = str.split(',').map((p) => Number.parseFloat(p.trim()));
    if (parts.length === 4 && parts.every((p) => !Number.isNaN(p))) {
        return { minLat: parts[0], minLon: parts[1], maxLat: parts[2], maxLon: parts[3] };
    }
    return undefined;
};

export const queryToDestinations = (destinations?: string | string[]): Destination[] => {
    const list = Array.isArray(destinations) ? destinations : destinations ? [destinations] : [];
    return list
        .map((entry): Destination | undefined => {
            const [label, country, bboxStr] = entry.split(DESTINATION_DELIMITER);
            const boundingBox = parseBoundingBox(bboxStr);
            if (!boundingBox) return undefined;
            return { label, boundingBox, country: country || undefined };
        })
        .filter((d) => d !== undefined);
};

export const serializeBoundingBox = (b: BoundingBox): string => `${b.minLat},${b.minLon},${b.maxLat},${b.maxLon}`;

export const destinationsToApiQuery = (destinations: Destination[]): Record<string, string[]> | undefined => {
    if (destinations.length === 0) return undefined;
    const countries = [...new Set(destinations.map((d) => d.country).filter((c): c is string => !!c))];
    return {
        bbox: destinations.map((d) => serializeBoundingBox(d.boundingBox)),
        ...(countries.length > 0 && { country: countries }),
    };
};

export const destinationsToQuery = (destinations: Destination[]): string[] | null =>
    destinations.length === 0
        ? null
        : destinations.map((d) => [d.label, d.country, serializeBoundingBox(d.boundingBox)].join(DESTINATION_DELIMITER));
